import { describesVideo, readFaceMap, type FaceMap } from '@gymcloud/faces'

/*
 * Reading a set's face map, and whether a copy with faces blurred can be made here — the
 * small part of `/specs/features/faces.spec.md` every page needs, kept apart from the making of
 * the copy so the encoder is only loaded when a copy is made.
 */

/** Why a copy with faces blurred was not handed over. */
export class FacesHeldBack extends Error {
  readonly reason: 'not-found' | 'cannot-here'

  constructor(reason: 'not-found' | 'cannot-here') {
    super(
      reason === 'not-found'
        ? 'Its faces have not been found'
        : 'This browser cannot blur faces in a copy',
    )
    this.name = 'FacesHeldBack'
    this.reason = reason
  }
}

/** Whether this browser can make a copy with faces blurred: it needs WebCodecs and an offscreen canvas. */
export function canHideFacesHere(): boolean {
  return (
    typeof VideoEncoder !== 'undefined' &&
    typeof VideoDecoder !== 'undefined' &&
    typeof OffscreenCanvas !== 'undefined'
  )
}

/**
 * A set's face map, where it has one that describes the video it carries; null where it has
 * none. Not reaching the server is not having none: it throws, to be tried again.
 */
export async function fetchFaceMap(
  url: string,
  videoBytes: number | null | undefined,
): Promise<FaceMap | null> {
  const response = await fetch(url)
  if (response.status === 404) {
    return null
  }
  if (!response.ok) {
    throw new Error(String(response.status))
  }
  const map = readFaceMap(await response.json().catch(() => null))
  return map && describesVideo(map, videoBytes) ? map : null
}
